import { useState } from 'react';

/**
 * Format a date string (YYYY-MM-DD) to a readable format like "Mon, Jun 15"
 */
function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

/**
 * Format a time string (HH:MM) to a readable format like "9:00 AM"
 */
function formatTime(timeStr) {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${m} ${ampm}`;
}

/**
 * Check if a date string is in the past (overdue).
 */
function isOverdue(dateStr) {
  if (!dateStr) return false;
  const today = new Date().toISOString().split('T')[0];
  return dateStr < today;
}

/**
 * Days until a due date. Negative = overdue.
 */
function daysUntil(dateStr) {
  if (!dateStr) return Infinity;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const due = new Date(dateStr + 'T00:00:00');
  return Math.ceil((due - now) / (1000 * 60 * 60 * 24));
}

/* ─── Reminder Card ─── */

function ReminderCard({ reminder, onClick }) {
  const overdue = reminder.status === 'pending' && isOverdue(reminder.dueDate);
  const days = daysUntil(reminder.dueDate);
  const completed = reminder.status === 'completed';

  let dateLabel;
  if (completed) {
    dateLabel = 'Completed';
  } else if (days === 0) {
    dateLabel = 'Today';
  } else if (days === 1) {
    dateLabel = 'Tomorrow';
  } else if (days < 0) {
    dateLabel = `${Math.abs(days)}d overdue`;
  } else if (days <= 7) {
    dateLabel = `In ${days} days`;
  } else {
    dateLabel = formatDate(reminder.dueDate);
  }

  return (
    <button
      onClick={onClick}
      className="card"
      style={{
        width: '100%',
        padding: '14px 16px',
        textAlign: 'left',
        cursor: 'pointer',
        opacity: completed ? 0.6 : 1,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              fontWeight: 600,
              fontSize: '0.875rem',
              textDecoration: completed ? 'line-through' : 'none',
            }}>
              {reminder.title}
            </span>
            {overdue && (
              <span style={{
                fontSize: '0.625rem',
                fontWeight: 700,
                padding: '2px 7px',
                borderRadius: 100,
                background: 'rgba(239, 68, 68, 0.15)',
                color: '#ef4444',
                textTransform: 'uppercase',
              }}>
                overdue
              </span>
            )}
          </div>
          {reminder.dueTime && !completed && (
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
              {formatTime(reminder.dueTime)}
            </div>
          )}
        </div>
        <span style={{
          fontSize: '0.8125rem',
          fontWeight: 600,
          color: overdue ? '#ef4444' : completed ? 'var(--text-muted)' : 'var(--text-secondary)',
          flexShrink: 0,
        }}>
          {dateLabel}
        </span>
      </div>
    </button>
  );
}

/* ─── Reminder Form ─── */

function ReminderForm({ initial, onSave, onCancel, onDelete }) {
  const [title, setTitle] = useState(initial?.title || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [dueDate, setDueDate] = useState(initial?.dueDate || '');
  const [dueTime, setDueTime] = useState(initial?.dueTime || '');

  function handleSubmit(e) {
    e.preventDefault();
    if (!title.trim() || !dueDate) return;
    onSave({
      ...(initial || {}),
      title: title.trim(),
      description: description.trim(),
      dueDate,
      dueTime: dueTime || null,
    });
  }

  const inputStyle = {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid var(--border)',
    background: 'var(--bg-secondary)',
    color: 'var(--text-primary)',
    fontSize: '0.875rem',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--text-secondary)',
    marginBottom: 4,
  };

  return (
    <div className="card" style={{ padding: 20, overflow: 'hidden' }}>
      <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: '0 0 16px' }}>
        {initial?.id ? 'Edit Reminder' : 'New Reminder'}
      </h3>
      <form onSubmit={handleSubmit} style={{ overflow: 'hidden' }}>
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Title *</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Detail car, check tires, etc...."
            required
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional details..."
            rows={3}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </div>

        <div style={{ marginBottom: 14, overflow: 'hidden' }}>
          <label style={labelStyle}>Due Date *</label>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            required
            style={{ ...inputStyle, display: 'block', WebkitAppearance: 'none', appearance: 'none' }}
          />
        </div>

        <div style={{ marginBottom: 14, overflow: 'hidden' }}>
          <label style={labelStyle}>Time (optional)</label>
          <input
            type="time"
            value={dueTime}
            onChange={(e) => setDueTime(e.target.value)}
            style={{ ...inputStyle, display: 'block', WebkitAppearance: 'none', appearance: 'none' }}
          />
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button
            type="submit"
            disabled={!title.trim() || !dueDate}
            style={{
              flex: 1,
              padding: '10px 16px',
              borderRadius: 8,
              border: 'none',
              background: 'var(--accent)',
              color: '#fff',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: !title.trim() || !dueDate ? 'default' : 'pointer',
              opacity: !title.trim() || !dueDate ? 0.5 : 1,
            }}
          >
            {initial?.id ? 'Save Changes' : 'Add Reminder'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '10px 16px',
              borderRadius: 8,
              border: '1px solid var(--border)',
              background: 'transparent',
              color: 'var(--text-secondary)',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        </div>

        {initial?.id && onDelete && (
          <button
            type="button"
            onClick={() => onDelete(initial.id)}
            style={{
              width: '100%',
              marginTop: 12,
              padding: '10px 16px',
              borderRadius: 8,
              border: '1px solid rgba(239, 68, 68, 0.3)',
              background: 'transparent',
              color: '#ef4444',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Delete Reminder
          </button>
        )}
      </form>
    </div>
  );
}

/* ─── Reminder Detail ─── */

function ReminderDetail({ reminder, onEdit, onComplete, onDelete, onBack, canService }) {
  const overdue = reminder.status === 'pending' && isOverdue(reminder.dueDate);
  const completed = reminder.status === 'completed';

  return (
    <div>
      <button
        onClick={onBack}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          background: 'none',
          border: 'none',
          color: 'var(--text-secondary)',
          fontSize: '0.8125rem',
          fontWeight: 600,
          cursor: 'pointer',
          padding: '4px 0',
          marginBottom: 12,
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Back
      </button>

      <div className="card" style={{ padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: 700, margin: 0, flex: 1 }}>
            {reminder.title}
          </h3>
          {overdue && (
            <span style={{
              fontSize: '0.625rem',
              fontWeight: 700,
              padding: '3px 8px',
              borderRadius: 100,
              background: 'rgba(239, 68, 68, 0.15)',
              color: '#ef4444',
              textTransform: 'uppercase',
            }}>
              overdue
            </span>
          )}
          {completed && (
            <span style={{
              fontSize: '0.625rem',
              fontWeight: 700,
              padding: '3px 8px',
              borderRadius: 100,
              background: 'rgba(34, 197, 94, 0.15)',
              color: '#22c55e',
              textTransform: 'uppercase',
            }}>
              completed
            </span>
          )}
        </div>

        {reminder.description && (
          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: '0 0 16px', lineHeight: 1.5 }}>
            {reminder.description}
          </p>
        )}

        <div style={{ display: 'flex', gap: 20, marginBottom: 16, fontSize: '0.8125rem' }}>
          <div>
            <div style={{ color: 'var(--text-muted)', fontSize: '0.6875rem', fontWeight: 600, marginBottom: 2 }}>Due Date</div>
            <div style={{ fontWeight: 600, color: overdue ? '#ef4444' : 'var(--text-primary)' }}>
              {formatDate(reminder.dueDate)}
            </div>
          </div>
          {reminder.dueTime && (
            <div>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.6875rem', fontWeight: 600, marginBottom: 2 }}>Time</div>
              <div style={{ fontWeight: 600 }}>{formatTime(reminder.dueTime)}</div>
            </div>
          )}
        </div>

        {completed && reminder.completedAt && (
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 16 }}>
            Completed {new Date(reminder.completedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
        )}

        {canService && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {!completed && (
              <>
                <button
                  onClick={() => onEdit(reminder)}
                  style={{
                    flex: 1,
                    padding: '10px 16px',
                    borderRadius: 8,
                    border: '1px solid var(--border)',
                    background: 'transparent',
                    color: 'var(--text-primary)',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Edit
                </button>
                <button
                  onClick={() => onComplete(reminder.id)}
                  style={{
                    flex: 1,
                    padding: '10px 16px',
                    borderRadius: 8,
                    border: 'none',
                    background: 'var(--accent)',
                    color: '#fff',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Mark Complete
                </button>
              </>
            )}
            <button
              onClick={() => onDelete(reminder.id)}
              style={{
                flex: completed ? 1 : undefined,
                padding: '10px 16px',
                borderRadius: 8,
                border: '1px solid rgba(239, 68, 68, 0.3)',
                background: 'transparent',
                color: '#ef4444',
                fontSize: '0.8125rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Reminder See All ─── */

function ReminderSeeAll({ reminders, onSelect, onBack }) {
  const [tab, setTab] = useState('upcoming');

  const upcoming = reminders
    .filter((r) => r.status === 'pending')
    .sort((a, b) => (a.dueDate || '').localeCompare(b.dueDate || ''));

  const completed = reminders
    .filter((r) => r.status === 'completed')
    .sort((a, b) => (b.completedAt || '').localeCompare(a.completedAt || ''));

  const list = tab === 'upcoming' ? upcoming : completed;

  return (
    <div>
      <button
        onClick={onBack}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          background: 'none',
          border: 'none',
          color: 'var(--text-secondary)',
          fontSize: '0.8125rem',
          fontWeight: 600,
          cursor: 'pointer',
          padding: '4px 0',
          marginBottom: 12,
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Back
      </button>

      <h2 style={{
        fontSize: '0.8125rem',
        fontWeight: 600,
        color: 'var(--text-secondary)',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        margin: '0 0 12px',
      }}>
        All Reminders
      </h2>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)' }}>
        {['upcoming', 'completed'].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              flex: 1,
              padding: '8px 16px',
              border: 'none',
              background: tab === t ? 'var(--accent)' : 'transparent',
              color: tab === t ? '#fff' : 'var(--text-secondary)',
              fontSize: '0.8125rem',
              fontWeight: 600,
              cursor: 'pointer',
              textTransform: 'capitalize',
            }}
          >
            {t} ({t === 'upcoming' ? upcoming.length : completed.length})
          </button>
        ))}
      </div>

      {list.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: '0.875rem' }}>
          {tab === 'upcoming' ? 'No upcoming reminders' : 'No completed reminders'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {list.map((r) => (
            <ReminderCard key={r.id} reminder={r} onClick={() => onSelect(r)} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Main Reminders Component ─── */

export default function Reminders({ vehicleId, reminders = [], onAdd, onUpdate, onComplete, onDelete, canLog, canService, onToast }) {
  // View state: 'list' | 'form' | 'detail' | 'seeAll'
  const [view, setView] = useState('list');
  const [selectedReminder, setSelectedReminder] = useState(null);
  const [editingReminder, setEditingReminder] = useState(null);

  const pendingReminders = reminders
    .filter((r) => r.status === 'pending')
    .sort((a, b) => (a.dueDate || '').localeCompare(b.dueDate || ''));

  // Show max 3 in the list view
  const visibleReminders = pendingReminders.slice(0, 3);
  const hasMore = reminders.length > 3;

  function handleSave(data) {
    if (editingReminder?.id) {
      onUpdate(editingReminder.id, data);
      onToast?.('Reminder updated');
    } else {
      onAdd(data);
      onToast?.('Reminder added');
    }
    setView('list');
    setEditingReminder(null);
  }

  function handleDelete(id) {
    onDelete(id);
    onToast?.('Reminder deleted');
    setView('list');
    setSelectedReminder(null);
    setEditingReminder(null);
  }

  function handleComplete(id) {
    onComplete(id);
    onToast?.('Reminder completed');
    setView('list');
    setSelectedReminder(null);
  }

  // ─── Form View ───
  if (view === 'form') {
    return (
      <div style={{ marginBottom: 20 }}>
        <ReminderForm
          initial={editingReminder}
          onSave={handleSave}
          onCancel={() => { setView('list'); setEditingReminder(null); }}
          onDelete={editingReminder?.id && canService ? handleDelete : null}
        />
      </div>
    );
  }

  // ─── Detail View ───
  if (view === 'detail' && selectedReminder) {
    return (
      <div style={{ marginBottom: 20 }}>
        <ReminderDetail
          reminder={selectedReminder}
          onEdit={(r) => { setEditingReminder(r); setView('form'); }}
          onComplete={handleComplete}
          onDelete={handleDelete}
          onBack={() => { setView('list'); setSelectedReminder(null); }}
          canService={canService}
        />
      </div>
    );
  }

  // ─── See All View ───
  if (view === 'seeAll') {
    return (
      <div style={{ marginBottom: 20 }}>
        <ReminderSeeAll
          reminders={reminders}
          onSelect={(r) => { setSelectedReminder(r); setView('detail'); }}
          onBack={() => setView('list')}
        />
      </div>
    );
  }

  // ─── List View (default) ───
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2
          style={{
            fontSize: '0.8125rem',
            fontWeight: 600,
            color: 'var(--text-secondary)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            margin: 0,
          }}
        >
          Your Reminders
        </h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {hasMore && (
            <button
              onClick={() => setView('seeAll')}
              style={{
                padding: '4px 10px',
                borderRadius: 100,
                border: '1px solid var(--border)',
                background: 'transparent',
                color: 'var(--text-secondary)',
                fontSize: '0.6875rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              See All ({reminders.length})
            </button>
          )}
          {canLog && (
            <button
              onClick={() => { setEditingReminder(null); setView('form'); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '4px 10px',
                borderRadius: 100,
                border: '1px solid var(--accent)',
                background: 'transparent',
                color: 'var(--accent)',
                fontSize: '0.6875rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add
            </button>
          )}
        </div>
      </div>

      {visibleReminders.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
          {canLog ? 'No reminders yet — tap Add to create one' : 'No reminders set'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {visibleReminders.map((r) => (
            <ReminderCard
              key={r.id}
              reminder={r}
              onClick={() => { setSelectedReminder(r); setView('detail'); }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
